import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Typography, Button, Space, Card, Statistic, Row, Col,
  DatePicker, Tag, InputNumber, Popconfirm, message, Tooltip, Modal, Input,
} from 'antd';
import {
  CheckCircleOutlined, ClockCircleOutlined, EditOutlined, CheckOutlined, CloseOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from '../../services/api';

const { Title, Text } = Typography;

const fmtMoney = (v) => v != null ? `£${Number(v).toFixed(2)}` : '—';

const DriverWages = () => {
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [wages, setWages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingRateId, setEditingRateId] = useState(null);
  const [editingRateValue, setEditingRateValue] = useState(null);
  const [payNotes, setPayNotes] = useState('');
  const [payingId, setPayingId] = useState(null);

  const monthStr = selectedMonth.format('YYYY-MM');

  const fetchWages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.driverWages.getWages(monthStr);
      setWages(res.data || []);
    } catch {
      message.error('Failed to load wages');
    } finally {
      setLoading(false);
    }
  }, [monthStr]);

  useEffect(() => {
    fetchWages();
  }, [fetchWages]);

  const handleMarkPaid = async (row) => {
    try {
      await apiService.driverWages.markPaid({
        driver: row._id,
        month: monthStr,
        daysWorked: row.daysWorked,
        dailyRate: row.dailyRate,
        amount: row.amount,
        notes: payNotes,
      });
      message.success(`${row.name} marked as paid`);
      setPayingId(null);
      setPayNotes('');
      fetchWages();
    } catch {
      message.error('Failed to mark as paid');
    }
  };

  const handleUnmarkPaid = async (row) => {
    try {
      await apiService.driverWages.unmarkPaid(row._id, monthStr);
      message.success('Payment undone');
      fetchWages();
    } catch {
      message.error('Failed to undo payment');
    }
  };

  const saveRate = async (row) => {
    try {
      await apiService.driverWages.updateRate(row._id, editingRateValue);
      message.success('Rate updated');
      setEditingRateId(null);
      fetchWages();
    } catch {
      message.error('Failed to update rate');
    }
  };

  const totalAmount = wages.reduce((s, r) => s + r.amount, 0);
  const totalPaid   = wages.filter(r => r.paid).reduce((s, r) => s + r.amount, 0);
  const totalUnpaid = totalAmount - totalPaid;
  const unpaidCount = wages.filter(r => !r.paid).length;

  const columns = [
    {
      title: 'Driver',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <Text strong>{name}</Text>,
    },
    {
      title: 'Daily Rate',
      dataIndex: 'dailyRate',
      key: 'dailyRate',
      align: 'right',
      width: 160,
      render: (rate, record) => {
        if (editingRateId === record._id) {
          return (
            <Space size={4}>
              <InputNumber
                size="small"
                prefix="£"
                min={0}
                step={5}
                value={editingRateValue}
                onChange={setEditingRateValue}
                style={{ width: 90 }}
                autoFocus
              />
              <Button size="small" type="text" icon={<CheckOutlined style={{ color: '#3f8600' }} />} onClick={() => saveRate(record)} />
              <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => setEditingRateId(null)} />
            </Space>
          );
        }
        return (
          <Space size={4}>
            <span>{fmtMoney(rate)}</span>
            <Tooltip title="Edit rate">
              <Button
                size="small"
                type="text"
                icon={<EditOutlined style={{ fontSize: 11, color: '#aaa' }} />}
                onClick={() => { setEditingRateId(record._id); setEditingRateValue(rate); }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'Days Worked',
      dataIndex: 'daysWorked',
      key: 'daysWorked',
      align: 'center',
      width: 110,
    },
    {
      title: 'Wages',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      width: 110,
      render: fmtMoney,
    },
    {
      title: 'Status',
      key: 'status',
      align: 'center',
      width: 120,
      render: (_, record) =>
        record.paid ? (
          <Tooltip title={`Paid on ${dayjs(record.payment?.paidAt).format('DD MMM YYYY')}`}>
            <Tag icon={<CheckCircleOutlined />} color="success">Paid</Tag>
          </Tooltip>
        ) : (
          <Tag icon={<ClockCircleOutlined />} color="warning">Unpaid</Tag>
        ),
    },
    {
      title: 'Action',
      key: 'action',
      align: 'right',
      width: 130,
      render: (_, record) =>
        record.paid ? (
          <Popconfirm
            title="Undo this payment?"
            onConfirm={() => handleUnmarkPaid(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button size="small" danger type="text">Undo</Button>
          </Popconfirm>
        ) : (
          <Button
            size="small"
            type="primary"
            onClick={() => { setPayingId(record._id); setPayNotes(''); }}
          >
            Mark Paid
          </Button>
        ),
    },
  ];

  const payingRow = wages.find(r => r._id === payingId);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0 }}>Driver Wages</Title>
        <DatePicker
          picker="month"
          value={selectedMonth}
          onChange={(d) => d && setSelectedMonth(d)}
          allowClear={false}
          format="MMMM YYYY"
          style={{ width: 160 }}
        />
      </div>

      {/* Summary cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title="Total Wages" value={fmtMoney(totalAmount)} valueStyle={{ fontSize: 20 }} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Paid"
              value={fmtMoney(totalPaid)}
              valueStyle={{ color: '#3f8600', fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title={`Unpaid (${unpaidCount} driver${unpaidCount !== 1 ? 's' : ''})`}
              value={fmtMoney(totalUnpaid)}
              valueStyle={{ color: totalUnpaid > 0 ? '#cf1322' : undefined, fontSize: 20 }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        title={
          <Space>
            <span>{selectedMonth.format('MMMM YYYY')} — Wages</span>
            {unpaidCount > 0 && (
              <Tag color="warning">{unpaidCount} unpaid</Tag>
            )}
          </Space>
        }
      >
        <Table
          dataSource={wages}
          columns={columns}
          rowKey="_id"
          loading={loading}
          pagination={false}
          size="small"
          locale={{ emptyText: 'No drivers worked this month' }}
          onRow={(record) => ({
            onClick: (e) => {
              // Don't navigate if clicking action buttons or rate editor
              if (e.target.closest('button') || e.target.closest('.ant-input-number')) return;
              const start = selectedMonth.startOf('month').format('YYYY-MM-DD');
              const end   = selectedMonth.endOf('month').format('YYYY-MM-DD');
              navigate(`/daily-runs/driver-detail?name=${encodeURIComponent(record.name)}&driverId=${record._id}&start=${start}&end=${end}`);
            },
            style: { cursor: 'pointer' },
          })}
          summary={() =>
            wages.length > 0 ? (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Text strong>Total</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <Text strong>{fmtMoney(totalAmount)}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} colSpan={2} />
              </Table.Summary.Row>
            ) : null
          }
        />
      </Card>

      {/* Mark paid confirmation modal */}
      <Modal
        open={!!payingId}
        title={`Mark ${payingRow?.name} as paid`}
        onCancel={() => setPayingId(null)}
        onOk={() => payingRow && handleMarkPaid(payingRow)}
        okText="Confirm Payment"
      >
        {payingRow && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <Text type="secondary">Days worked</Text>
              <Text strong>{payingRow.daysWorked}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <Text type="secondary">Daily rate</Text>
              <Text strong>{fmtMoney(payingRow.dailyRate)}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
              <Text type="secondary">Total wages</Text>
              <Text strong style={{ fontSize: 16 }}>{fmtMoney(payingRow.amount)}</Text>
            </div>
            <Input.TextArea
              placeholder="Notes (optional)"
              value={payNotes}
              onChange={e => setPayNotes(e.target.value)}
              rows={2}
              style={{ marginTop: 8 }}
            />
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default DriverWages;
